import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationPage from '../components/NotificationPage';
import { getUsername } from '../utils/userUtils';
import { useAuth } from '../contexts/AuthContext';
import { consumeUnauthorizeReturnPath } from '@/utils/authRedirect';

const TOKEN_CHECK_INTERVAL_MS = 5000;

const getNameFromUser = (user: unknown): string | undefined => {
  if (!user || typeof user !== 'object') return undefined;
  const record = user as Record<string, unknown>;

  const username = record.username;
  if (typeof username === 'string' && username.trim()) return username;

  const name = record.name;
  if (typeof name === 'string' && name.trim()) return name;

  return undefined;
};

const Unauthorized = () => {
  const { user, microsoftToken, isAuthenticated, login, getGromToken } = useAuth();
  const navigate = useNavigate();

  // Prefer MSAL account (accounts[0]) when available; fall back to stored account info (Teams flow)
  const userName =
    getNameFromUser(user) ||
    getUsername() ||
    'Unknown User';

  // This page can be hit directly (outside ProtectedRoute), so microsoftToken may not
  // have been acquired yet. Trigger the same silent/interactive login ProtectedRoute uses.
  useEffect(() => {
    if (!isAuthenticated) {
      login().catch((error) => console.error('Login failed on unauthorize page:', error));
    }
  }, [isAuthenticated, login]);

  // Poll /token every 5s; once it returns 200, the session is healthy again → go back.
  // Goes through AuthContext.getGromToken so gromToken state/localStorage is set too,
  // otherwise ProtectedRoute would keep showing its own loading screen after navigate.
  useEffect(() => {
    if (!microsoftToken) return;

    let cancelled = false;

    const checkToken = async () => {
      try {
        await getGromToken(microsoftToken);
        if (cancelled) return;
        navigate(consumeUnauthorizeReturnPath(), { replace: true });
      } catch {
        // still unauthorized, keep polling
      }
    };

    const intervalId = window.setInterval(checkToken, TOKEN_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [microsoftToken, getGromToken, navigate]);

  return (
    <NotificationPage
      content={
        <>
          <p>User {userName} lacks of privilege to access reports.</p>
          <p>Please contact your administrator for further information.</p>
        </>
      }
      enableRotation={false}
    />
  );
};

export default Unauthorized;
